import React from 'react';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Separator } from '../../../ui/separator';
import { Plus, X } from 'lucide-react';
import { formatSizeInput } from '../../../../lib/utils';
import { RATE_TYPES, PLYWOOD_SIZES, DOOR_SIZES_WITH_SQFT } from '../../constants';
import SizeBuilderMixed from '../../SizeBuilderMixed';

/**
 * Plywood-style bulk-thickness section.
 *
 * Rendered when the parent detects ``isPlywoodBulkMode`` — i.e. the current
 * ``main_category`` is one of the "sheet + thickness" categories (Plywood,
 * Blockboard, MDF, Doors, WPC/PVC, ACP). Lets the admin enter per-thickness
 * rates across every available thickness for the brand/subcategory in a
 * single form submission, plus tick which sheet sizes are stocked and add
 * custom thicknesses/sizes on the fly.
 */
export function PlywoodBulkSection({
  form,
  setForm,
  getAllThicknessesSorted,
  getAllSizesSorted,
  plywoodSizes,
  setPlywoodSizes,
  customSizes,
  setCustomSizes,
  customThicknesses,
  setCustomThicknesses,
  plywoodRates,
  setPlywoodRates,
  newThickness,
  setNewThickness,
  addCustomThickness,
  newSize,
  setNewSize,
  toggleSize,
}) {
  const addCustomSize = () => {
    const sizeValue = formatSizeInput(newSize).toLowerCase();
    if (sizeValue && sizeValue.includes('x')) {
      const parts = sizeValue.split('x');
      // For doors: sizes are in inches, so sqft = (h * w) / 144
      // For others: sizes are in feet, so sqft = h * w
      const sqft = form.main_category === 'Doors'
        ? Math.round((parseFloat(parts[0]) * parseFloat(parts[1]) / 144) * 100) / 100
        : Math.round(parseFloat(parts[0]) * parseFloat(parts[1]) * 10) / 10;
      const sizeId = sizeValue.replace(/\s/g, '');
      const displayLabel = form.main_category === 'Doors'
        ? `${parts[0].trim()}" x ${parts[1].trim()}"`
        : sizeValue.toUpperCase();
      const baseSizes = form.main_category === 'Doors' ? DOOR_SIZES_WITH_SQFT : PLYWOOD_SIZES;
      if (!baseSizes.find(s => s.id === sizeId) && !customSizes.find(s => s.id === sizeId)) {
        setCustomSizes([...customSizes, { id: sizeId, label: displayLabel, sqft }]);
        setPlywoodSizes([...plywoodSizes, sizeId]);
      }
      setNewSize('');
    }
  };

  return (
    <>
      <Separator />
      <div className="bg-[#F2F4EF] p-3 rounded-lg">
        <Label className="text-xs font-semibold uppercase tracking-wide text-[#556B2F]">
          Enter Rates per Sq. Ft (Leave empty if not available)
          {form.main_category && form.main_category !== 'Plywood' && (
            <span className="ml-2 text-[#DAA520]">- {form.main_category}</span>
          )}
        </Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
          {/* Sorted thicknesses: standard + custom in ascending order */}
          {getAllThicknessesSorted(form.main_category).map(t => {
            const isCustom = customThicknesses.includes(t.id);
            return (
              <div key={t.id} className="flex items-center gap-2">
                <span className={`text-xs font-medium w-20 ${isCustom ? 'text-[#DAA520]' : 'text-[#5C6150]'}`}>
                  {t.label}
                </span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Rate"
                  value={plywoodRates[t.id] || ''}
                  onChange={e => setPlywoodRates(r => ({ ...r, [t.id]: e.target.value }))}
                  className="h-8 text-sm font-mono-data flex-1"
                />
                {isCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-[#B93838]"
                    onClick={() => setCustomThicknesses(c => c.filter(x => x !== t.id))}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Add custom thickness */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E2E6D9]">
          <Input
            placeholder="Add custom thickness (e.g., 15.5MM)"
            value={newThickness}
            onChange={e => setNewThickness(e.target.value.toUpperCase())}
            className="h-8 text-sm flex-1"
            onKeyDown={e => e.key === 'Enter' && addCustomThickness()}
          />
          <Button variant="outline" size="sm" onClick={addCustomThickness} className="h-8">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Size Selection - Sorted by area (largest first) */}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6150]">
          Available Sizes {form.main_category === 'Doors' && <span className="text-[#8F9485]">(in inches)</span>}
        </Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {getAllSizesSorted().map(size => {
            const isCustom = customSizes.some(cs => cs.id === size.id);
            return (
              <label
                key={size.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                  plywoodSizes.includes(size.id)
                    ? isCustom
                      ? 'bg-[#DAA520] text-white border-[#DAA520]'
                      : 'bg-[#556B2F] text-white border-[#556B2F]'
                    : isCustom
                      ? 'bg-white border-[#DAA520] hover:bg-[#DAA520]/10'
                      : 'bg-white border-[#E2E6D9] hover:border-[#556B2F]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={plywoodSizes.includes(size.id)}
                  onChange={() => toggleSize(size.id)}
                  className="hidden"
                />
                <span className="text-sm font-medium">{size.label}</span>
                <span className={`text-xs ${plywoodSizes.includes(size.id) ? 'text-white/70' : 'text-[#8F9485]'}`}>({size.sqft.toFixed(2)} sq.ft)</span>
                {isCustom && (
                  <button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCustomSizes(customSizes.filter(s => s.id !== size.id));
                      setPlywoodSizes(plywoodSizes.filter(s => s !== size.id));
                    }}
                    className="ml-1 text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                )}
              </label>
            );
          })}
        </div>

        {/* Per-dimension Unit Builder — pick numbers AND unit per
            side, e.g. 4ft × 6in. Builds a size string like "4ft x 6in" that
            `sizeToSqft` parses natively, and computes the sqft display via
            the same central helper so the catalog is always consistent
            with the cart math. Admin-only. */}
        <div className="mt-3 p-2 bg-[#F2F4EF] border border-[#E2E6D9] rounded-md" data-testid="size-builder-mixed">
          <p className="text-[10px] uppercase tracking-wide text-[#5C6150] mb-1.5">Size Builder (pick per-dimension units)</p>
          <SizeBuilderMixed
            onAdd={(sizeStr, sqftValue, label) => {
              const sizeId = sizeStr.replace(/\s/g, '').toLowerCase();
              if (!customSizes.find(s => s.id === sizeId)) {
                setCustomSizes([...customSizes, { id: sizeId, label, sqft: sqftValue }]);
                setPlywoodSizes([...plywoodSizes, sizeId]);
              }
            }}
          />
        </div>

        {/* Add Custom Size */}
        <div className="flex gap-2 mt-3">
          <Input
            value={newSize}
            onChange={e => setNewSize(formatSizeInput(e.target.value))}
            placeholder={form.main_category === 'Doors' ? 'Type size: 90 40 → 90x40' : 'Type size: 9.2 4 → 9.2x4'}
            className="flex-1 text-sm"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomSize();
              }
            }}
            data-testid="custom-size-input"
          />
          <span className="text-[10px] text-[#8F9485] self-center whitespace-nowrap">Space → x</span>
          <Button
            variant="outline"
            onClick={addCustomSize}
            className="border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F] hover:text-white"
            data-testid="add-custom-size-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Rate Type for multi-thickness products */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Rate Type</Label>
          <Select value={form.rate_type || 'per sq ft'} onValueChange={v => setForm(f => ({ ...f, rate_type: v }))}>
            <SelectTrigger className="mt-1 text-sm" data-testid="form-rate-type-bulk"><SelectValue /></SelectTrigger>
            <SelectContent>{RATE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {form.rate_type === 'per sq ft' && (
          <div>
            <Label className="text-xs">Size Unit</Label>
            <Select value={form.size_unit || 'feet'} onValueChange={v => setForm(f => ({ ...f, size_unit: v }))}>
              <SelectTrigger className="mt-1 text-sm" data-testid="form-size-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feet">Feet</SelectItem>
                <SelectItem value="inches">Inches</SelectItem>
                <SelectItem value="mm">Millimeters (mm)</SelectItem>
                <SelectItem value="meters">Meters</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {form.rate_type === 'per box' && (
          <div>
            <Label className="text-xs">Pieces per Box</Label>
            <Input
              type="number"
              placeholder="e.g., 100"
              value={form.pieces_per_box || ''}
              onChange={e => setForm(f => ({ ...f, pieces_per_box: parseInt(e.target.value, 10) || 0 }))}
              className="mt-1 text-sm"
              data-testid="form-pieces-per-box"
            />
          </div>
        )}
      </div>
    </>
  );
}
